import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Building2,
  MapPin,
  Phone,
  Sparkles,
  Star,
  Loader2,
  ExternalLink,
  CheckCircle2,
  AlertCircle,
  Image as ImageLucide,
} from "lucide-react";
import { Navbar } from "../components/app/Navbar";
import { Footer } from "../components/app/Footer";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { useAuth } from "../context/AuthContext";
import { useT } from "../context/LanguageContext";
import { BookingPagePhotos } from "../components/app/BookingPagePhotos";
import api from "../lib/api";
import { toast } from "sonner";

const US_STATE_CODES = [
  "AL","AK","AZ","AR","CA","CO","CT","DE","DC","FL","GA","HI","ID","IL","IN",
  "IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH",
  "NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT",
  "VT","VA","WA","WV","WI","WY","PR","VI","GU",
];

// Canonical highlight keys — must match BOOKING_HIGHLIGHT_KEYS on the backend.
const HIGHLIGHT_OPTIONS = [
  "insured",
  "locally_owned",
  "supplies_included",
  "pet_friendly",
  "recurring_available",
  "residential_cleaning",
  "commercial_cleaning",
  "english_speaking",
  "spanish_speaking",
];

// Canonical service values + labels. Must stay in sync with SERVICE_TYPES in
// PublicLeadForm.jsx and CANONICAL_SERVICE_TYPES on the backend — the
// canonical English value is what gets stored and filtered on.
const SERVICE_OPTIONS = [
  { value: "Standard Clean",      en: "Standard Clean",       es: "Limpieza estándar" },
  { value: "Deep Clean",          en: "Deep Clean",           es: "Limpieza profunda" },
  { value: "Move-Out",            en: "Move-Out",             es: "Mudanza (salida)" },
  { value: "Move-In",             en: "Move-In",              es: "Mudanza (entrada)" },
  { value: "Post-Construction",   en: "Post-Construction",    es: "Post-construcción" },
  { value: "Airbnb Turnover",     en: "Airbnb Turnover",      es: "Rotación Airbnb" },
  { value: "Office / Commercial", en: "Office / Commercial",  es: "Oficina / Comercial" },
];
const ALL_SERVICE_VALUES = SERVICE_OPTIONS.map((s) => s.value);

const SectionCard = ({ icon: Icon, title, description, children, testid }) => (
  <section
    className="rounded-2xl bg-white border border-gray-200 shadow-sm overflow-hidden"
    data-testid={testid}
  >
    <header className="px-6 sm:px-7 py-5 border-b border-gray-100 flex items-start gap-4">
      <span className="w-10 h-10 rounded-lg bg-green-50 border border-green-100 flex items-center justify-center shrink-0">
        <Icon className="w-5 h-5 text-green-800" />
      </span>
      <div>
        <h2 className="font-display text-lg font-semibold text-gray-900 tracking-tight">{title}</h2>
        {description ? (
          <p className="text-sm text-gray-500 mt-0.5 leading-relaxed">{description}</p>
        ) : null}
      </div>
    </header>
    <div className="px-6 sm:px-7 py-6">{children}</div>
  </section>
);

export default function BookingPageSettings() {
  const { user, setUser, refreshMe } = useAuth();
  const { t, lang } = useT();

  const [businessName, setBusinessName] = useState("");
  const [tagline, setTagline] = useState("");
  const [taglineEs, setTaglineEs] = useState("");
  const [about, setAbout] = useState("");
  const [aboutEs, setAboutEs] = useState("");
  const [city, setCity] = useState("");
  const [stateVal, setStateVal] = useState("");
  const [serviceArea, setServiceArea] = useState("");
  const [serviceAreaEs, setServiceAreaEs] = useState("");
  const [publicPhone, setPublicPhone] = useState("");
  const [websiteUrl, setWebsiteUrl] = useState("");
  const [googleRating, setGoogleRating] = useState("");
  const [googleCount, setGoogleCount] = useState("");
  const [highlights, setHighlights] = useState([]);
  // Never-customized owners default to ALL services checked (legacy = all).
  const [enabledServices, setEnabledServices] = useState(ALL_SERVICE_VALUES);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState({ tone: "", text: "" });

  useEffect(() => {
    if (user) {
      setBusinessName(user.business_name || "");
      setTagline(user.booking_tagline || "");
      setTaglineEs(user.booking_tagline_es || "");
      setAbout(user.booking_about || "");
      setAboutEs(user.booking_about_es || "");
      setCity(user.booking_city || "");
      setStateVal(user.booking_state || "");
      setServiceArea(user.booking_service_area || "");
      setServiceAreaEs(user.booking_service_area_es || "");
      setPublicPhone(user.public_phone || "");
      setWebsiteUrl(user.website_url || "");
      setGoogleRating(user.google_rating != null ? String(user.google_rating) : "");
      setGoogleCount(user.google_review_count != null ? String(user.google_review_count) : "");
      setHighlights(Array.isArray(user.booking_highlights) ? user.booking_highlights : []);
      setEnabledServices(
        Array.isArray(user.enabled_services) && user.enabled_services.length > 0
          ? user.enabled_services.filter((s) => ALL_SERVICE_VALUES.includes(s))
          : ALL_SERVICE_VALUES
      );
    }
  }, [user]);

  useEffect(() => {
    refreshMe();
  }, [refreshMe]);

  const toggleHighlight = (key) => {
    setHighlights((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const toggleService = (value) => {
    setEnabledServices((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    );
  };

  const save = async (e) => {
    e.preventDefault();
    setMsg({ tone: "", text: "" });
    const trimmedName = businessName.trim();
    if (!trimmedName) {
      setMsg({ tone: "error", text: t("settings.businessNameEmpty") });
      return;
    }
    if (enabledServices.length === 0) {
      setMsg({ tone: "error", text: t("bps.servicesMin") });
      toast.error(t("bps.servicesMin"));
      return;
    }
    const trimmedWebsite = websiteUrl.trim();
    if (trimmedWebsite && !/^https?:\/\//i.test(trimmedWebsite)) {
      setMsg({ tone: "error", text: t("settings.invalidUrl") });
      return;
    }
    const ratingStr = googleRating.trim();
    let ratingNum = null;
    if (ratingStr !== "") {
      ratingNum = Number(ratingStr);
      if (!Number.isFinite(ratingNum) || ratingNum < 0 || ratingNum > 5) {
        setMsg({ tone: "error", text: t("bps.ratingInvalid") });
        return;
      }
    }
    const countStr = googleCount.trim();
    let countNum = null;
    if (countStr !== "") {
      countNum = Number(countStr);
      if (!Number.isInteger(countNum) || countNum < 0) {
        setMsg({ tone: "error", text: t("bps.countInvalid") });
        return;
      }
    }
    setSaving(true);
    try {
      // NOTE: deliberately does NOT send `phone` / `sms_consent` — the
      // private SMS notification number is managed only from Settings.
      const { data } = await api.patch("/auth/me", {
        business_name: trimmedName,
        booking_tagline: tagline,
        booking_about: about,
        booking_tagline_es: taglineEs,
        booking_about_es: aboutEs,
        booking_city: city,
        booking_state: stateVal,
        booking_service_area: serviceArea,
        booking_service_area_es: serviceAreaEs,
        public_phone: publicPhone,
        website_url: trimmedWebsite,
        google_rating: ratingNum,
        google_review_count: countNum,
        booking_highlights: highlights,
        enabled_services: ALL_SERVICE_VALUES.filter((v) => enabledServices.includes(v)),
      });
      setUser(data);
      try {
        localStorage.setItem("tasklio_user", JSON.stringify(data));
      } catch {
        // ignore storage errors — server is the truth.
      }
      setMsg({ tone: "success", text: t("bps.saved") });
      toast.success(t("bps.saved"));
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setMsg({
        tone: "error",
        text: typeof detail === "string" ? detail : t("bps.saveError"),
      });
    } finally {
      setSaving(false);
    }
  };

  const previewUrl = user?.id ? `/l/${user.id}?preview=1` : null;

  return (
    <div className="min-h-screen flex flex-col bg-[#f9f9f8]" data-testid="booking-page-settings">
      <Navbar />
      <main className="flex-1 max-w-3xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
        <div className="mb-8 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div>
            <h1
              className="font-display text-3xl sm:text-4xl font-semibold text-gray-900 tracking-tight"
              data-testid="bps-title"
            >
              {t("bps.title")}
            </h1>
            <p className="mt-2 text-gray-500">{t("bps.subtitle")}</p>
          </div>
          {previewUrl && (
            <a href={previewUrl} target="_blank" rel="noreferrer" className="shrink-0">
              <Button
                variant="outline"
                className="h-11 px-5 font-semibold border-gray-300 bg-white text-gray-900 hover:bg-gray-50 w-full sm:w-auto"
                data-testid="bps-preview-button"
              >
                <ExternalLink className="w-4 h-4 mr-1.5" /> {t("bps.preview")}
              </Button>
            </a>
          )}
        </div>

        {/* Logo + work photos — uploads apply instantly, independent of the
            text-fields save form below. */}
        <div className="mb-6">
          <SectionCard
            icon={ImageLucide}
            title={t("bps.sectionPhotos")}
            description={t("bps.sectionPhotosHelp")}
            testid="bps-photos-card"
          >
            <BookingPagePhotos />
          </SectionCard>
        </div>

        <form onSubmit={save} className="space-y-6">
          {/* Business Intro */}
          <SectionCard
            icon={Building2}
            title={t("bps.sectionIntro")}
            description={t("bps.sectionIntroHelp")}
            testid="bps-intro-card"
          >
            <div className="space-y-5">
              <div>
                <Label htmlFor="bps-business-name">{t("bps.businessName")}</Label>
                <Input
                  id="bps-business-name"
                  value={businessName}
                  onChange={(e) => setBusinessName(e.target.value)}
                  required
                  className="mt-2 h-11"
                  data-testid="bps-business-name-input"
                />
              </div>
              <div>
                <Label htmlFor="bps-tagline">{t("bps.tagline")}</Label>
                <Input
                  id="bps-tagline"
                  value={tagline}
                  onChange={(e) => setTagline(e.target.value)}
                  maxLength={160}
                  placeholder={t("bps.taglinePlaceholder")}
                  className="mt-2 h-11"
                  data-testid="bps-tagline-input"
                />
              </div>
              <div>
                <Label htmlFor="bps-tagline-es">{t("bps.taglineEs")}</Label>
                <Input
                  id="bps-tagline-es"
                  value={taglineEs}
                  onChange={(e) => setTaglineEs(e.target.value)}
                  maxLength={160}
                  placeholder={t("bps.taglineEsPlaceholder")}
                  className="mt-2 h-11"
                  data-testid="bps-tagline-es-input"
                />
                <p className="mt-1.5 text-xs text-gray-500">{t("bps.esFallbackHint")}</p>
              </div>
              <div>
                <Label htmlFor="bps-about">{t("bps.about")}</Label>
                <Textarea
                  id="bps-about"
                  rows={5}
                  value={about}
                  onChange={(e) => setAbout(e.target.value)}
                  maxLength={2000}
                  placeholder={t("bps.aboutPlaceholder")}
                  className="mt-2"
                  data-testid="bps-about-input"
                />
              </div>
              <div>
                <Label htmlFor="bps-about-es">{t("bps.aboutEs")}</Label>
                <Textarea
                  id="bps-about-es"
                  rows={5}
                  value={aboutEs}
                  onChange={(e) => setAboutEs(e.target.value)}
                  maxLength={2000}
                  placeholder={t("bps.aboutEsPlaceholder")}
                  className="mt-2"
                  data-testid="bps-about-es-input"
                />
                <p className="mt-1.5 text-xs text-gray-500">{t("bps.esFallbackHint")}</p>
              </div>
            </div>
          </SectionCard>

          {/* Service Area */}
          <SectionCard
            icon={MapPin}
            title={t("bps.sectionArea")}
            description={t("bps.sectionAreaHelp")}
            testid="bps-area-card"
          >
            <div className="space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-[1fr_140px] gap-4">
                <div>
                  <Label htmlFor="bps-city">{t("bps.city")}</Label>
                  <Input
                    id="bps-city"
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    placeholder="Atlanta"
                    className="mt-2 h-11"
                    data-testid="bps-city-input"
                  />
                </div>
                <div>
                  <Label htmlFor="bps-state">{t("bps.state")}</Label>
                  <select
                    id="bps-state"
                    value={stateVal}
                    onChange={(e) => setStateVal(e.target.value)}
                    className="mt-2 h-11 w-full rounded-md border border-gray-200 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-green-700"
                    data-testid="bps-state-select"
                  >
                    <option value="">—</option>
                    {US_STATE_CODES.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <Label htmlFor="bps-service-area">{t("bps.serviceArea")}</Label>
                <Input
                  id="bps-service-area"
                  value={serviceArea}
                  onChange={(e) => setServiceArea(e.target.value)}
                  maxLength={200}
                  placeholder={t("bps.serviceAreaPlaceholder")}
                  className="mt-2 h-11"
                  data-testid="bps-service-area-input"
                />
              </div>
              <div>
                <Label htmlFor="bps-service-area-es">{t("bps.serviceAreaEs")}</Label>
                <Input
                  id="bps-service-area-es"
                  value={serviceAreaEs}
                  onChange={(e) => setServiceAreaEs(e.target.value)}
                  maxLength={200}
                  placeholder={t("bps.serviceAreaEsPlaceholder")}
                  className="mt-2 h-11"
                  data-testid="bps-service-area-es-input"
                />
                <p className="mt-1.5 text-xs text-gray-500">{t("bps.esFallbackHint")}</p>
              </div>
            </div>
          </SectionCard>

          {/* Public Contact */}
          <SectionCard
            icon={Phone}
            title={t("bps.sectionContact")}
            description={t("bps.sectionContactHelp")}
            testid="bps-contact-card"
          >
            <div className="space-y-5">
              <div>
                <Label htmlFor="bps-public-phone">{t("bps.publicPhone")}</Label>
                <Input
                  id="bps-public-phone"
                  value={publicPhone}
                  onChange={(e) => setPublicPhone(e.target.value)}
                  placeholder="+1 555 123 4567"
                  className="mt-2 h-11"
                  data-testid="bps-public-phone-input"
                />
                <p className="mt-1.5 text-xs text-gray-500" data-testid="bps-public-phone-helper">
                  {t("bps.publicPhoneHelper")}
                </p>
              </div>
              <div>
                <Label htmlFor="bps-website">{t("bps.website")}</Label>
                <Input
                  id="bps-website"
                  type="url"
                  inputMode="url"
                  value={websiteUrl}
                  onChange={(e) => setWebsiteUrl(e.target.value)}
                  placeholder="https://yourbusiness.com"
                  className="mt-2 h-11"
                  data-testid="bps-website-input"
                />
              </div>
            </div>
          </SectionCard>

          {/* Services You Offer */}
          <SectionCard
            icon={Sparkles}
            title={t("bps.sectionServices")}
            description={t("bps.sectionServicesHelp")}
            testid="bps-services-card"
          >
            <div className="flex items-center justify-between gap-3 mb-3">
              <p className="text-xs text-gray-500">{t("bps.servicesHint")}</p>
              <button
                type="button"
                onClick={() => setEnabledServices(ALL_SERVICE_VALUES)}
                className="text-sm font-medium text-green-800 hover:underline shrink-0"
                data-testid="bps-services-select-all"
              >
                {t("bps.selectAll")}
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
              {SERVICE_OPTIONS.map((svc) => {
                const checked = enabledServices.includes(svc.value);
                return (
                  <label
                    key={svc.value}
                    className={`flex items-center gap-2.5 px-3.5 py-3 rounded-xl border cursor-pointer transition-colors ${
                      checked
                        ? "border-green-300 bg-green-50"
                        : "border-gray-200 bg-white hover:bg-gray-50"
                    }`}
                    data-testid={`bps-service-${svc.value.replace(/[^a-zA-Z]+/g, "-").toLowerCase()}`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleService(svc.value)}
                      className="w-4 h-4 shrink-0 accent-green-800"
                      data-testid={`bps-service-checkbox-${svc.value.replace(/[^a-zA-Z]+/g, "-").toLowerCase()}`}
                    />
                    <span className="text-sm font-medium text-gray-800">
                      {svc[lang] || svc.en}
                    </span>
                  </label>
                );
              })}
            </div>
            {enabledServices.length === 0 && (
              <p className="mt-3 text-sm text-red-600" data-testid="bps-services-min-error">
                {t("bps.servicesMin")}
              </p>
            )}
          </SectionCard>

          {/* Business Highlights */}
          <SectionCard
            icon={Sparkles}
            title={t("bps.sectionHighlights")}
            description={t("bps.sectionHighlightsHelp")}
            testid="bps-highlights-card"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
              {HIGHLIGHT_OPTIONS.map((key) => {
                const checked = highlights.includes(key);
                return (
                  <label
                    key={key}
                    className={`flex items-center gap-2.5 px-3.5 py-3 rounded-xl border cursor-pointer transition-colors ${
                      checked
                        ? "border-green-300 bg-green-50"
                        : "border-gray-200 bg-white hover:bg-gray-50"
                    }`}
                    data-testid={`bps-highlight-${key}`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleHighlight(key)}
                      className="w-4 h-4 shrink-0 accent-green-800"
                      data-testid={`bps-highlight-checkbox-${key}`}
                    />
                    <span className="text-sm font-medium text-gray-800">
                      {t(`public.highlight.${key}`)}
                    </span>
                  </label>
                );
              })}
            </div>
          </SectionCard>

          {/* Google Business */}
          <SectionCard
            icon={Star}
            title={t("bps.sectionGoogle")}
            description={t("bps.sectionGoogleHelp")}
            testid="bps-google-card"
          >
            <div className="space-y-5">
              {/* Google Review Link — read-only, single source of truth is
                  users.google_review_link (edited in Settings). Shown FIRST
                  so it's obvious the page is already connected. */}
              <div data-testid="bps-review-link-status">
                <Label>{t("bps.reviewLinkLabel")}</Label>
                <div className="mt-2 rounded-xl border border-gray-200 bg-gray-50/70 px-4 py-3">
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
                    <div className="min-w-0">
                      {user?.google_review_link ? (
                        <>
                          <p className="text-sm font-medium text-green-800 flex items-center gap-1.5">
                            <CheckCircle2 className="w-4 h-4 shrink-0" />
                            <span data-testid="bps-review-link-connected">{t("bps.reviewLinkConnected")}</span>
                          </p>
                          <p
                            className="mt-1 text-sm text-gray-700 break-all"
                            data-testid="bps-review-link-url"
                          >
                            {user.google_review_link}
                          </p>
                          <p className="mt-1 text-xs text-gray-500">{t("bps.reviewLinkConnectedHelp")}</p>
                        </>
                      ) : (
                        <>
                          <p className="text-sm font-medium text-gray-800 flex items-center gap-1.5">
                            <AlertCircle className="w-4 h-4 text-gray-400 shrink-0" />
                            <span data-testid="bps-review-link-missing">{t("bps.reviewLinkMissing")}</span>
                          </p>
                          <p className="mt-1 text-xs text-gray-500">{t("bps.reviewLinkMissingHelp")}</p>
                        </>
                      )}
                    </div>
                    <Link
                      to="/settings"
                      className="text-sm font-semibold text-green-800 hover:underline shrink-0"
                      data-testid="bps-review-link-settings-action"
                    >
                      {user?.google_review_link ? t("bps.editInSettings") : t("bps.addInSettings")}
                    </Link>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="bps-google-rating">{t("bps.googleRating")}</Label>
                  <Input
                    id="bps-google-rating"
                    type="number"
                    step="0.1"
                    min="0"
                    max="5"
                    value={googleRating}
                    onChange={(e) => setGoogleRating(e.target.value)}
                    placeholder="4.9"
                    className="mt-2 h-11"
                    data-testid="bps-google-rating-input"
                  />
                </div>
                <div>
                  <Label htmlFor="bps-google-count">{t("bps.googleReviewCount")}</Label>
                  <Input
                    id="bps-google-count"
                    type="number"
                    step="1"
                    min="0"
                    value={googleCount}
                    onChange={(e) => setGoogleCount(e.target.value)}
                    placeholder="87"
                    className="mt-2 h-11"
                    data-testid="bps-google-count-input"
                  />
                </div>
              </div>
              <p className="text-xs text-gray-500" data-testid="bps-google-helper">
                {t("bps.googleHelper")}
              </p>
            </div>
          </SectionCard>

          {msg.text && (
            <p
              className={`flex items-start gap-2 text-sm border rounded-md px-3 py-2 ${
                msg.tone === "error"
                  ? "text-red-700 bg-red-50 border-red-100"
                  : "text-green-800 bg-green-50 border-green-100"
              }`}
              data-testid="bps-message"
            >
              {msg.tone === "error" ? (
                <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
              ) : (
                <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />
              )}
              <span>{msg.text}</span>
            </p>
          )}

          <div className="flex flex-col sm:flex-row gap-3">
            <Button
              type="submit"
              disabled={saving}
              className="h-12 px-7 bg-green-800 hover:bg-green-900 text-white font-semibold"
              data-testid="bps-save-button"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : t("common.saveChanges")}
            </Button>
          </div>
        </form>

        <p className="mt-10 text-center text-sm text-gray-400">
          <Link to="/settings" className="hover:underline" data-testid="bps-back-to-settings">
            {t("bps.backToSettings")}
          </Link>
        </p>
      </main>
      <Footer />
    </div>
  );
}
